import {
  Outlet,
  createFileRoute,
  redirect,
  retainSearchParams,
  stripSearchParams,
} from '@tanstack/react-router'
import { isEqual, size } from 'lodash/fp'
import * as v from 'valibot'

import { compactObject } from '@masterplandev/utils'

import { buildPageTitle } from '@/core/utils/buildPageTitle/buildPageTitle'
import { getLocalStorageItem } from '@/core/utils/localStorage/getLocalStorageItem'
import { updateLocalStorageItem } from '@/core/utils/localStorage/updateLocalStorageItem'
import { LearnpathsContentFullForm } from '@/learnpaths-assigned-page/components/LearnpathsContentFullForm/LearnpathsContentFullForm'
import useScormConfiguration from '@/scorm/hooks/useScormConfiguration'

const storageKey = 'assigned-courses-filters'

const searchSchema = v.object({
  categoryId: v.optional(v.string()),
  state: v.optional(v.picklist(['active', 'completed'])),
  types: v.optional(v.array(v.picklist(['linear', 'certificate']))),
  query: v.optional(v.string()),
})

type Filters = v.InferOutput<typeof searchSchema>

export const retainListFilters = retainSearchParams<Filters>(
  Object.keys(searchSchema.entries) as Array<keyof Filters>,
)

const defaultFilters: v.InferOutput<typeof searchSchema> = {
  categoryId: undefined,
  state: undefined,
  types: [],
  query: '',
}

export const Route = createFileRoute(
  '/_learning/_company/_list/company-courses',
)({
  head: () => ({
    meta: [
      {
        title: buildPageTitle({
          titleKey: 'page-title-my-company-courses',
        }),
      },
    ],
  }),
  validateSearch: searchSchema,
  beforeLoad: ({ cause, location, search: { categoryId, state, types } }) => {
    const searchFilters = { categoryId, state, types }
    const storedFilters = getLocalStorageItem(storageKey)
    const hasSearchFilters = !!size(compactObject(searchFilters))
    const hasStoredFilters = !!size(compactObject(storedFilters))

    if (cause === 'stay' && !isEqual(storedFilters, searchFilters)) {
      updateLocalStorageItem(storageKey, searchFilters)
    }

    if (cause !== 'enter') return
    if (!location.state?.restoreFilters) return
    if (hasSearchFilters) return
    if (!hasStoredFilters) return

    throw redirect({
      to: '/company-courses',
      search: storedFilters,
      replace: true,
    })
  },
  search: {
    middlewares: [stripSearchParams(defaultFilters)],
  },
  component: RouteComponent,
})

function RouteComponent() {
  const { learnpathId } = useScormConfiguration()

  // Single learnpath view is only available from within Masterplan SCORM
  // package and was designed for SCORM viewers.
  return learnpathId ? <Outlet /> : <LearnpathsContentFullForm />
}
